import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  MapPin, 
  Briefcase, 
  GraduationCap, 
  Calendar,
  Phone,
  Mail,
  Heart,
  Flag,
  CheckCircle,
  AlertCircle,
  User,
  Home,
  IndianRupee
} from 'lucide-react';
import { Profile } from '../../types';
import { useAuth } from '../../hooks/useAuth';
import apiService from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const ProfileDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [showReportModal, setShowReportModal] = useState(false);

  useEffect(() => {
    if (id) {
      loadProfile(id);
    }
  }, [id]);

  const loadProfile = async (profileId: string) => {
    setLoading(true);
    try {
      const response = await apiService.getProfile(profileId);
      setProfile(response.profile);
    } catch (error) {
      console.error('Failed to load profile:', error);
      navigate('/profiles');
    } finally {
      setLoading(false);
    }
  };

  const getVerificationBadge = (status: string) => {
    switch (status) {
      case 'verified':
        return (
          <div className="flex items-center space-x-1 text-green-600">
            <CheckCircle className="w-4 h-4" />
            <span className="text-sm font-medium">Verified Profile</span>
          </div>
        );
      case 'semi-verified':
        return (
          <div className="flex items-center space-x-1 text-yellow-600">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm font-medium">Semi-verified Profile</span>
          </div>
        );
      default:
        return (
          <div className="flex items-center space-x-1 text-gray-600">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm font-medium">Not Verified</span>
          </div>
        );
    }
  };

  const formatLocation = (workLocation: any) => {
    if (workLocation.type === 'nri') {
      return `${workLocation.country?.replace('-', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())} (NRI)`;
    }
    const state = workLocation.state?.replace('-', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
    const city = workLocation.city?.replace('-', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
    return `${city}, ${state}`;
  };

  const formatText = (text: string) => {
    return text.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // FIXED: Check both current user AND profile owner verification status
  const currentUserVerified = user?.verificationStatus === 'verified';
  const profileOwnerVerified = profile?.verificationStatus === 'verified';
  const canViewFullDetails = currentUserVerified && profileOwnerVerified;
  const canViewPhotos = canViewFullDetails;

  if (loading) {
    return <LoadingSpinner fullScreen text="Loading profile..." />;
  }

  if (!profile) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Profile not found</h2>
        <Link to="/profiles" className="text-primary-600 hover:text-primary-700">
          Back to profiles
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Back Button */}
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Profiles</span>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Photos */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg border border-gray-200 p-6 sticky top-6">
            {canViewPhotos && profile.photos && profile.photos.length > 0 ? (
              <>
                <div className="mb-4">
                  <img
                    src={profile.photos[currentPhotoIndex]}
                    alt={`${profile.name} - Photo ${currentPhotoIndex + 1}`}
                    className="w-full h-64 object-cover rounded-lg"
                  />
                </div>
                
                {profile.photos.length > 1 && (
                  <div className="grid grid-cols-4 gap-2">
                    {profile.photos.map((photo, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentPhotoIndex(index)}
                        className={`relative h-16 rounded-lg overflow-hidden border-2 ${
                          currentPhotoIndex === index ? 'border-primary-500' : 'border-gray-200'
                        }`}
                      >
                        <img
                          src={photo}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <div className="w-24 h-24 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <User className="w-12 h-12 text-primary-600" />
                </div>
                <p className="text-sm text-gray-600">
                  {!canViewPhotos 
                    ? 'Complete verification to view photos'
                    : 'No photos available'
                  }
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="mt-6 space-y-3">
              <button className="w-full bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center space-x-2">
                <Heart className="w-4 h-4" />
                <span>Show Interest</span>
              </button>
              
              <button 
                onClick={() => setShowReportModal(true)}
                className="w-full border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2"
              >
                <Flag className="w-4 h-4" />
                <span>Report Profile</span>
              </button>
            </div>
          </div>
        </div>

        {/* Right Column - Profile Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-1">
                  {profile.name}
                </h1>
                <div className="flex items-center space-x-4 text-sm text-gray-600">
                  <div className="flex items-center space-x-1">
                    <Calendar className="w-4 h-4" />
                    <span>{profile.age} years old</span>
                  </div>
                  <span>•</span>
                  <span className="capitalize">{profile.height.replace('.', '\'')}"</span>
                  <span>•</span>
                  <span className="capitalize">{formatText(profile.complexion)}</span>
                </div>
              </div>
              {getVerificationBadge(profile.verificationStatus)}
            </div>

            {/* Contact Information - FIXED: Show phone and email correctly */}
            {canViewFullDetails ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {profile.phone && (
                  <div className="flex items-center space-x-2 text-sm">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span>{profile.phone}</span>
                  </div>
                )}
                {profile.email && (
                  <div className="flex items-center space-x-2 text-sm">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span>{profile.email}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-yellow-700 text-center">
                  {!currentUserVerified 
                    ? 'Complete manual verification to view contact details'
                    : !profileOwnerVerified 
                    ? 'This user is pending manual verification. Contact information will be available once verified.'
                    : 'Complete profile verification to view contact details'
                  }
                </p>
              </div>
            )}

            {/* Personal Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="text-sm text-gray-500">Marital Status</span>
                <p className="font-medium capitalize">{formatText(profile.maritalStatus)}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Caste</span>
                <p className="font-medium capitalize">
                  {formatText(profile.caste)}
                  {profile.subcaste && ` • ${profile.subcaste}`}
                </p>
              </div>
              {/* FIXED: Only show gothra to verified users viewing verified profiles */}
              {canViewFullDetails && profile.gothra && (
                <div>
                  <span className="text-sm text-gray-500">Gothra</span>
                  <p className="font-medium">{profile.gothra}</p>
                </div>
              )}
            </div>
          </div>

          {/* Professional Information */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
              <Briefcase className="w-5 h-5 text-primary-600" />
              <span>Professional Information</span>
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="text-sm text-gray-500">Education</span>
                <p className="font-medium flex items-center space-x-1">
                  <GraduationCap className="w-4 h-4 text-gray-400" />
                  <span>{formatText(profile.education)}</span>
                </p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Occupation</span>
                <p className="font-medium">{formatText(profile.occupation)}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Annual Income</span>
                <p className="font-medium flex items-center space-x-1">
                  <IndianRupee className="w-4 h-4 text-gray-400" />
                  <span>{profile.annualIncome} LPA</span>
                </p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Work Location</span>
                <p className="font-medium flex items-center space-x-1">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span>{formatLocation(profile.workLocation)}</span>
                </p>
              </div>
            </div>
          </div>

          {/* Family Information */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
              <Home className="w-5 h-5 text-primary-600" />
              <span>Family Information</span>
            </h2>
            
            <div>
              <span className="text-sm text-gray-500">Family Location</span>
              <p className="font-medium flex items-center space-x-1 mb-3">
                <MapPin className="w-4 h-4 text-gray-400" />
                <span>
                  {formatText(profile.familyLocation.city)}, {formatText(profile.familyLocation.state)}
                </span>
              </p>
              
              {profile.familyLocation.address && (
                <div>
                  <span className="text-sm text-gray-500">Address</span>
                  <p className="text-sm text-gray-700">{profile.familyLocation.address}</p>
                </div>
              )}
            </div>
          </div>

          {/* About & Expectations */}
          {(profile.selfSummary || profile.expectations) && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">About & Expectations</h2>
              
              {profile.selfSummary && (
                <div className="mb-4">
                  <h3 className="font-medium text-gray-900 mb-2">About Me</h3>
                  <p className="text-gray-700 leading-relaxed">{profile.selfSummary}</p>
                </div>
              )}
              
              {profile.expectations && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Partner Expectations</h3>
                  <p className="text-gray-700 leading-relaxed">{profile.expectations}</p>
                </div>
              )}
            </div>
          )}

          {/* Verification Information */}
          {profile.aadhaarVerified && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-green-800 font-medium">Document Verified</span>
              </div>
              <p className="text-green-700 text-sm mt-1">
                This profile has been verified with official documents
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileDetail;